import React from 'react';
import { css } from '@emotion/react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth } from '../contexts/AuthContext';
import { useHistory, useParams } from 'react-router-dom';
import { useLoading } from '../contexts/LoadingContext';
import { selfDataSelector, selfDataSlice } from '../redux/config/selfData';
import { currentNursingHomeSlice } from '../redux/config/currentNursingHome';
import api from '../api';
import dayjs from 'dayjs';
import { LeftNav } from '../components/leftNav';
import { GlobalHeader } from '../components/globalHeader';
import { IGetNursingHomeRes } from '../types/api/nursingHome/getNursingHome';
import { filterConditionSelector, residentListSettingsSlice } from '../redux/config/residentListSettings';
import { navSlice } from '../redux/config/leftnav';
import { AxiosError } from 'axios';

interface IErrorResponse {
  error: string;
}

// professional, medicalの共通の通知カバー種別を抽出した型
type BadgeResident = {
  bigBladderNotification: boolean;
  gotUpNotification: boolean;
  sensorErrorNotification: boolean;
  upsideDownNotification: boolean;
  urinatedNotification: boolean;
  zeroResetNotification: boolean;
  urineResetNotification: boolean;
};

const AppWrapper = css`
  display: flex;
  font-size: 14px;
  height: 100vh;
  width: 100%;
`;

const ContentWrapper = css`
  width: 100%;
  overflow: hidden;
`;

const ScrollContainer = css`
  width: 100%;
  height: calc(100vh - 60px);
  overflow: auto;
  background-color: var(--surface-content);
`;

export const DefaultLayout: React.FC = ({ children }) => {
  const { nursingHomeId } = useParams<{ nursingHomeId: string }>();
  const { unitId } = useSelector(filterConditionSelector);
  const { selfUser } = useSelector(selfDataSelector);
  const { currentUser } = useAuth();
  const { showLoading, hideLoading } = useLoading();
  const dispatch = useDispatch();
  const history = useHistory();
  const [isOpenLeftnav, setIsOpenLeftnav] = React.useState(true);
  const [nursingHomeList, setNursingHomeList] = React.useState<IGetNursingHomeRes[] | null>(null);

  const fetchBadge = React.useCallback(
    async (
      currentNursingHomeId = nursingHomeId,
      currentUnitId = unitId,
      currentServiceType: string = selfUser.serviceType,
    ) => {
      const params = currentUnitId
        ? { nursingHomeId: currentNursingHomeId, nursingHomeUnitId: currentUnitId }
        : { nursingHomeId: currentNursingHomeId };
      const residents: BadgeResident[] = (
        currentServiceType === 'medical'
          ? await api.get('/traversal/md/nursing-homes/:nursingHomeId/residents', { params })
          : await api.get('/traversal/nursing-homes/:nursingHomeId', { params })
      ).residents;
      const careSchedule = await api.get('/nursing-homes/:nursingHomeId/care-schedule', { params });
      const orders = await api.get('/nursing-homes/:nursingHomeId/orders', {
        params: { nursingHomeId: currentNursingHomeId, nursingHomeUnitId: currentUnitId, sort: 'desc', limit: 2000 },
      });
      // 48時間以内のアドバイス件数を取得
      const advices = await api.get('/nursing-homes/:nursingHomeId/advices', {
        params: {
          nursingHomeId: currentNursingHomeId,
          nursingHomeUnitId: currentUnitId,
          from: dayjs().subtract(48, 'hour').toISOString(),
          to: dayjs().toISOString(),
          limit: 200,
        },
      });
      const badge = {
        resident: residents.filter(
          (resident) =>
            resident.bigBladderNotification ||
            resident.gotUpNotification ||
            resident.sensorErrorNotification ||
            resident.upsideDownNotification ||
            resident.urinatedNotification ||
            resident.zeroResetNotification ||
            resident.urineResetNotification,
        ).length,
        careSchedule: careSchedule.length,
        temporarilySaved: orders.filter((order) => order.orderStatus === 'temporarilySaved').length,
        advice: advices.length ?? 0,
      };
      dispatch(navSlice.actions.setBadge(badge));
    },
    [unitId, selfUser.serviceType],
  );

  const handleSetSelfUser = React.useCallback(async () => {
    if (currentUser.isPending || !currentUser) return;
    try {
      const self = selfUser.id ? selfUser : await api.get('/users/_self');
      if (!selfUser.id) {
        dispatch(selfDataSlice.actions.setSelfUser(self));
      }
      if (self.nursingHomeUnitId) {
        const selfUnit = await api.get('/nursing-home-units/:nursingHomeUnitId', {
          params: { nursingHomeUnitId: self.nursingHomeUnitId },
        });
        dispatch(selfDataSlice.actions.setSelfUnit(selfUnit));
      }
      const units = await api.get('/nursing-homes/:nursingHomeId/nursing-home-units', {
        params: { nursingHomeId },
      });
      dispatch(selfDataSlice.actions.setSelfUnits(units));

      const selfNursingHome = await api.get('/nursing-homes/:nursingHomeId', {
        params: { nursingHomeId: self.nursingHomeId },
      });

      const currentNursingHome = await api.get('/nursing-homes/:nursingHomeId', {
        params: { nursingHomeId },
      });

      // _selfのnursingHomeGroupIdとURLのIdから取得したnursingHomeGroupIdが異なる場合は404画面へ
      if (selfNursingHome.nursingHomeGroupId !== currentNursingHome.nursingHomeGroupId) {
        history.push('/404');
        return;
      } else {
        dispatch(selfDataSlice.actions.setSelfNursingHome(currentNursingHome));
        dispatch(
          currentNursingHomeSlice.actions.setCurrentNursingHome({
            ...currentNursingHome,
          }),
        );
      }

      // メンバー権限の場合は取得しない
      if (self.professionalRole !== 'member') {
        const nursingHomes = await api.get('/nursing-home-groups/:nursingHomeGroupId/nursing-homes', {
          params: { nursingHomeGroupId: currentNursingHome.nursingHomeGroupId },
        });
        setNursingHomeList(nursingHomes);
      }

      // localStorage周りの処理
      // unitIDを自身でもっている場合はLocalStorageに格納する
      if (self.nursingHomeUnitId) {
        localStorage.setItem('unit', String(self.nursingHomeUnitId));
      }
      const conditions =
        (localStorage.getItem('condition') as 'all' | 'deviceAssigned' | 'deviceAttached' | 'unit') ?? 'all';
      const myId = Number(localStorage.getItem('self')) ?? 0;
      const savedUnitId = localStorage.getItem('unit');

      let unitId = null;
      // 前回と異なるIDの場合はunitIdを消去
      if (+myId !== self.id) {
        localStorage.removeItem('unit');
        localStorage.removeItem('condition');
      }

      if (self.nursingHomeUnitId) {
        localStorage.setItem('unit', String(self.nursingHomeUnitId));
        unitId = self.nursingHomeUnitId;
      } else if (!savedUnitId || savedUnitId === 'null' || !units.map((r) => r.id).includes(+savedUnitId)) {
        unitId = null;
      } else {
        unitId = savedUnitId;
      }

      localStorage.setItem('self', String(self.id) ?? '0');
      dispatch(
        residentListSettingsSlice.actions.setFilterCondition({
          conditions: conditions ?? 'all',
          unitId: unitId ? +unitId : null,
        }),
      );
    } catch (err) {
      if (
        (err as AxiosError<IErrorResponse>).response &&
        (err as AxiosError<IErrorResponse>).response?.status === 403
      ) {
        hideLoading();
        history.push('/404');
        return;
      }
    }
  }, [currentUser, selfUser]);

  const handleOpenLeftnav = React.useCallback(() => {
    setIsOpenLeftnav(true);
  }, []);

  const handleCloseLeftnav = React.useCallback(() => {
    setIsOpenLeftnav(false);
  }, []);

  React.useEffect(() => {
    handleSetSelfUser();
  }, [currentUser]);

  React.useEffect(() => {
    if (!selfUser.id) return;
    fetchBadge(undefined, selfUser.nursingHomeUnitId ?? null, selfUser.serviceType);
    const timer = setInterval(
      () => {
        fetchBadge(undefined, selfUser.nursingHomeUnitId ?? null, selfUser.serviceType);
      },
      1000 * 60 * 5,
    );
    return () => {
      clearInterval(timer);
    };
  }, [fetchBadge, selfUser.id, selfUser.nursingHomeUnitId, selfUser.serviceType]);

  return (
    <div css={AppWrapper}>
      {isOpenLeftnav && <LeftNav isOpenLeftnav={isOpenLeftnav} onClose={handleCloseLeftnav} />}
      <div css={ContentWrapper}>
        <GlobalHeader
          nursingHomes={nursingHomeList}
          isOpenLeftnav={isOpenLeftnav}
          onOpen={handleOpenLeftnav}
          fetchBadge={fetchBadge}
        />
        {currentUser && (
          <div css={ScrollContainer}>{children}</div>
        )}
      </div>
    </div>
  );
};
